import type {
  AgeCategory,
  ClientDTO,
  ClientTitle,
  ClientType,
  Language,
  UpdateClientRequest,
} from '@brainkey/shared';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { errorMessage, normalizePhone } from '../lib/format';
import { useUpdateClient } from '../lib/hooks/useClients';

import { Alert } from './ui/Alert';
import { Button } from './ui/Button';
import { ClientCodeField } from './ui/ClientCodeField';
import { CountryField } from './ui/CountryField';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { PhoneNumberField } from './ui/PhoneNumberField';
import { Select } from './ui/Select';

const TITLE_OPTIONS: { value: ClientTitle | ''; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'MR', label: 'Mr' },
  { value: 'MRS', label: 'Mrs' },
  { value: 'MS', label: 'Ms' },
  { value: 'MISS', label: 'Miss' },
  { value: 'DR', label: 'Dr' },
  { value: 'REV', label: 'Rev' },
  { value: 'PROF', label: 'Prof' },
];

interface EditClientModalProps {
  open: boolean;
  client: ClientDTO;
  onClose: () => void;
  onUpdated: (client: ClientDTO) => void;
}

interface FormState {
  title: ClientTitle | '';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  secondaryPhone: string;
  languagePref: Language;
  ageCategory: AgeCategory | '';
  clientType: ClientType | '';
  clientCode: string;
  country: string;
  city: string;
}

function toForm(c: ClientDTO): FormState {
  return {
    title: c.title ?? '',
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email ?? '',
    phone: c.phone ?? '',
    secondaryPhone: c.secondaryPhone ?? '',
    languagePref: c.languagePref,
    ageCategory: c.ageCategory ?? '',
    clientType: c.clientType,
    clientCode: c.clientCode,
    country: c.country ?? '',
    city: c.city ?? '',
  };
}

export function EditClientModal({
  open,
  client,
  onClose,
  onUpdated,
}: EditClientModalProps): JSX.Element {
  const [form, setForm] = useState<FormState>(() => toForm(client));
  const [error, setError] = useState<string | null>(null);
  const updateClient = useUpdateClient(client.id);

  // Re-sync when a different client is opened for editing.
  useEffect(() => {
    if (open) {
      setForm(toForm(client));
      setError(null);
    }
  }, [open, client]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClose = (): void => {
    if (updateClient.isPending) return;
    setError(null);
    onClose();
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (form.firstName.trim() === '' || form.lastName.trim() === '') {
      setError('Please fill in the required fields.');
      return;
    }
    if (form.clientType === '' || form.clientCode.trim() === '') {
      setError('Please choose a client type and enter a client ID.');
      return;
    }

    const body: UpdateClientRequest = {
      title: form.title === '' ? null : form.title,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      languagePref: form.languagePref,
      email: form.email.trim() === '' ? null : form.email.trim(),
      phone: form.phone.trim() === '' ? null : normalizePhone(form.phone),
      secondaryPhone:
        form.secondaryPhone.trim() === '' ? null : normalizePhone(form.secondaryPhone),
      ageCategory: form.ageCategory === '' ? null : form.ageCategory,
      clientType: form.clientType,
      clientCode: form.clientCode.trim(),
      country: form.country.trim() === '' ? null : form.country.trim(),
      city: form.city.trim() === '' ? null : form.city.trim(),
    };

    try {
      const updated = await updateClient.mutateAsync(body);
      onUpdated(updated);
    } catch (err) {
      setError(errorMessage(err, 'Could not update the client.'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Edit client"
      description="Update this client's details and preferences."
      size="lg"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={handleClose} disabled={updateClient.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-client-form"
            loading={updateClient.isPending}
          >
            Save changes
          </Button>
        </div>
      }
    >
      <form
        id="edit-client-form"
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        className="flex flex-col gap-6"
      >
        {error !== null && <Alert tone="danger">{error}</Alert>}

        <div className="flex flex-col gap-5">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Select
              label="Title"
              optional
              value={form.title}
              onValueChange={(value) => set('title', value as ClientTitle | '')}
              options={TITLE_OPTIONS}
            />
            <Input
              label="First name"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              required
              autoFocus
            />
            <Input
              label="Last name"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              required
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 pt-6 border-t border-zinc-100 dark:border-white/10">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Contact Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Input
              label="Email"
              type="email"
              optional
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
            />
            <PhoneNumberField
              optional
              value={form.phone}
              onChange={(value) => set('phone', value)}
            />
            <PhoneNumberField
              label="Secondary Mobile"
              optional
              value={form.secondaryPhone}
              onChange={(value) => set('secondaryPhone', value)}
            />
            <CountryField
              optional
              value={form.country}
              onChange={(value) => set('country', value)}
            />
            <Input
              label="City"
              optional
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 pt-6 border-t border-zinc-100 dark:border-white/10">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Classification</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Select
              label="Client type"
              value={form.clientType}
              onValueChange={(value) => set('clientType', value as ClientType | '')}
              options={[
                { value: '', label: 'Select a client type…' },
                { value: 'LOCAL', label: 'Local' },
                { value: 'FOREIGN', label: 'Foreign' },
              ]}
            />
            <ClientCodeField
              label="Client ID"
              value={form.clientCode}
              onChange={(value) => set('clientCode', value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-5 pt-6 border-t border-zinc-100 dark:border-white/10">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Preferences</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Select
              label="Preferred language"
              value={form.languagePref}
              onValueChange={(value) => set('languagePref', value as Language)}
              options={[
                { value: 'EN', label: 'English' },
                { value: 'SI', label: 'Sinhala' },
                { value: 'TA', label: 'Tamil' },
              ]}
            />
            <Select
              label="Age category"
              optional
              value={form.ageCategory}
              onValueChange={(value) => set('ageCategory', value as AgeCategory | '')}
              options={[
                { value: '', label: 'Not specified' },
                { value: 'CHILD', label: 'Child' },
                { value: 'TEEN', label: 'Teen' },
                { value: 'ADULT', label: 'Adult' },
                { value: 'SENIOR', label: 'Senior' },
              ]}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
